import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Paper,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart, List } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

interface ChargerUtilizationSummary {
  total_chargers: number;
  available_chargers: number;
  offline_chargers: number;
  faulted_chargers: number;
  avg_utilization_rate: number;
  uptime_rate: number;
  total_sessions: number;
  charged_sessions: number;
  total_energy_kwh: number;
  avg_sessions_per_charger: number;
}

interface ChargerMetrics {
  charger_id: string;
  charger_name: string;
  site_id: string;
  status: string;
  is_available: boolean;
  is_offline: boolean;
  is_faulted: boolean;
  session_count: number;
  charged_session_count: number;
  utilization_rate: number;
  total_charging_hours: number;
  avg_session_duration_minutes: number;
  total_energy_kwh: number;
  sessions_per_day: number;
  last_heartbeat: string | null;
}

interface TimeSeriesData {
  date: string;
  session_count: number;
  charged_session_count: number;
  charging_hours: number;
}

interface StatusDistribution {
  status: string;
  count: number;
}

interface ChargerUtilizationReportData {
  summary: ChargerUtilizationSummary;
  chargers: ChargerMetrics[];
  time_series: TimeSeriesData[];
  status_distribution: StatusDistribution[];
}

const COLORS = ['#009A75', '#2196F3', '#FFB800', '#FF6B6B', '#9C27B0', '#00BCD4'];

const STATUS_COLORS: Record<string, string> = {
  available: '#4CAF50',
  charging: '#2196F3',
  preparing: '#00BCD4',
  finishing: '#009A75',
  offline: '#9E9E9E',
  faulted: '#FF6B6B',
  unavailable: '#FFA726',
  maintenance: '#FFB800',
};

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const ChargerUtilizationReport: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [chargerId, setChargerId] = useState('');

  // Filter options state
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [chargers, setChargers] = useState<Array<{ id: string; name: string }>>([]);

  // Tab state (0 = time series, 1 = charger details, 2 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Period type state (day/month)
  const [periodType, setPeriodType] = useState<'day' | 'month'>('day');

  // Table sorting state for time series
  const [orderBy, setOrderBy] = useState<keyof TimeSeriesData>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Table sorting state for charger details
  const [chargerOrderBy, setChargerOrderBy] = useState<keyof ChargerMetrics>('charger_name');
  const [chargerOrder, setChargerOrder] = useState<'asc' | 'desc'>('asc');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch sites when vendor changes
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with vendor_id parameter if vendor is selected
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;

        const response = await fetch(url, { headers });
        const data = await response.json();
        setSites(data.data || []);

        // Reset site selection if it's no longer in the filtered list
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, [vendorId]); // Re-fetch when vendor changes

  // Fetch chargers when vendor or site changes
  useEffect(() => {
    const fetchChargers = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with appropriate filter parameters
        let url = `${API_CONFIG.baseURL}/api/backend/reports/filters/chargers`;
        const params = new URLSearchParams();

        if (siteId) {
          params.append('site_id', siteId);
        } else if (vendorId) {
          params.append('vendor_id', vendorId);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, { headers });
        const data = await response.json();
        setChargers(data.data || []);

        // Reset charger selection if it's no longer in the filtered list
        if (chargerId && !data.data?.some((c: any) => c.id === chargerId)) {
          setChargerId('');
        }
      } catch (error) {
        console.error('Error fetching chargers:', error);
      }
    };

    fetchChargers();
  }, [vendorId, siteId]); // Re-fetch when vendor or site changes

  // Fetch data
  const { data, loading, error, refetch } = useReportData<ChargerUtilizationReportData>({
    endpoint: '/api/backend/reports/charger-utilization',
    filters: {
      start_date: startDate,
      end_date: endDate,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
      ...(chargerId && { charger_id: chargerId }),
    },
  });

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    if (!processedTimeSeriesData) return;

    exportToCSV(
      processedTimeSeriesData,
      'charger-utilization',
      [
        { key: 'date', label: periodType === 'day' ? 'Date' : 'Month', format: 'date' },
        ...(canSeeTotalSessions ? [{ key: 'session_count', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_session_count', label: 'Charged Sessions', format: 'number' },
        { key: 'charging_hours', label: 'Charging Hours', format: 'number' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data) return;

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (siteId) {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        filters.site = { id: siteId, name: selectedSite.name };
      }
    }

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    if (chargerId) {
      const selectedCharger = chargers.find(c => c.id === chargerId);
      if (selectedCharger) {
        filters.charger = { id: chargerId, name: selectedCharger.name };
      }
    }

    await exportToExcel(
      processedTimeSeriesData,
      'charger-utilization',
      {
        columns: [
          { key: 'date', label: periodType === 'day' ? translate('reports.table.date', { fallback: 'Date' }) : translate('reports.table.month', { fallback: 'Month' }), format: 'date' },
          ...(canSeeTotalSessions ? [{ key: 'session_count', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_session_count', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
          { key: 'charging_hours', label: translate('reports.table.chargingHours', { fallback: 'Charging Hours' }), format: 'number' },
        ],
        summary: {
          title: translate('reports.utilization.title', { fallback: 'Charger Utilization' }),
          data: [
            {
              label: translate('reports.kpi.totalChargers', { fallback: 'Total Chargers' }),
              value: formatNumber(data.summary.total_chargers, 0)
            },
            {
              label: translate('reports.kpi.avgUtilization', { fallback: 'Avg Utilization Rate' }),
              value: formatNumber(data.summary.avg_utilization_rate, 1) + '%'
            },
            {
              label: translate('reports.kpi.uptimeRate', { fallback: 'Uptime Rate' }),
              value: formatNumber(data.summary.uptime_rate, 1) + '%'
            },
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
            {
              label: translate('reports.kpi.totalEnergy', { fallback: 'Total Energy' }),
              value: formatNumber(data.summary.total_energy_kwh, 2) + ' kWh'
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Process time series data based on period type
  const processedTimeSeriesData = useMemo(() => {
    if (!data?.time_series) return [];
    if (periodType === 'day') return data.time_series;

    // Aggregate by month
    const monthlyMap = new Map<string, TimeSeriesData>();

    data.time_series.forEach((item) => {
      const month = item.date.substring(0, 7); // YYYY-MM

      if (!monthlyMap.has(month)) {
        monthlyMap.set(month, {
          date: month,
          session_count: 0,
          charged_session_count: 0,
          charging_hours: 0,
        });
      }

      const entry = monthlyMap.get(month)!;
      entry.session_count += item.session_count;
      entry.charged_session_count += item.charged_session_count;
      entry.charging_hours += item.charging_hours;
    });

    return Array.from(monthlyMap.values());
  }, [data?.time_series, periodType]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!processedTimeSeriesData) return [];

    return [...processedTimeSeriesData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [processedTimeSeriesData, orderBy, order]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!processedTimeSeriesData || processedTimeSeriesData.length === 0) {
      return {
        session_count: 0,
        charged_session_count: 0,
        charging_hours: 0,
      };
    }

    return {
      session_count: processedTimeSeriesData.reduce((sum, row) => sum + row.session_count, 0),
      charged_session_count: processedTimeSeriesData.reduce((sum, row) => sum + row.charged_session_count, 0),
      charging_hours: processedTimeSeriesData.reduce((sum, row) => sum + row.charging_hours, 0),
    };
  }, [processedTimeSeriesData]);

  const handleSort = (property: keyof TimeSeriesData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChargerSort = (property: keyof ChargerMetrics) => {
    const isAsc = chargerOrderBy === property && chargerOrder === 'asc';
    setChargerOrder(isAsc ? 'desc' : 'asc');
    setChargerOrderBy(property);
  };

  // Sort charger data
  const sortedChargers = useMemo(() => {
    if (!data?.chargers) return [];

    return [...data.chargers].sort((a, b) => {
      const aValue = a[chargerOrderBy];
      const bValue = b[chargerOrderBy];

      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (aValue < bValue) return chargerOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return chargerOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.chargers, chargerOrderBy, chargerOrder]);

  const topChargerChartData = useMemo(() => {
    if (!data?.chargers) return [];

    const byUtilization = [...data.chargers].sort((a, b) => b.utilization_rate - a.utilization_rate);
    return isMobile ? byUtilization.slice(0, 5) : byUtilization.slice(0, 10);
  }, [data?.chargers, isMobile]);

  // Format status for display
  const formatStatus = (status: unknown) => {
    const normalized = typeof status === 'string' ? status : String(status ?? '');
    if (!normalized) return '-';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).replace(/_/g, ' ');
  };

  return (
    <ReportLayout
      title={translate('reports.utilization.title', { fallback: 'Charger Utilization' })}
      description={translate('reports.utilization.subtitle', { fallback: 'Charger performance and availability metrics' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          siteId={siteId}
          onSiteIdChange={setSiteId}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          chargerId={chargerId}
          onChargerIdChange={setChargerId}
          sites={sites}
          vendors={vendors}
          chargers={chargers}
          showSiteFilter={true}
          showVendorFilter={true}
          showChargerFilter={true}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                px: { xs: 1.5, sm: 2 },
                pt: 1.5,
                minWidth: 0,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minWidth: 0,
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textTransform: 'none',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  },
                }}
              >
                <Tab icon={<TableChart />} iconPosition="start" label={translate('reports.tabs.timeSeries', { fallback: 'Time Series' })} />
                <Tab icon={<List />} iconPosition="start" label={translate('reports.chargerDetails', { fallback: 'Charger Details' })} />
                <Tab icon={<ShowChart />} iconPosition="start" label={translate('reports.tabs.charts', { fallback: 'Charts' })} />
              </Tabs>

              {/* Period Toggle - Only show on Time Series tab */}
              {tabValue === 0 && (
                <ToggleButtonGroup
                  value={periodType}
                  exclusive
                  onChange={(_, newValue) => newValue && setPeriodType(newValue)}
                  size="small"
                  sx={{
                    mb: 1,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      flex: { xs: 1, sm: 'unset' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 0.5 },
                    },
                  }}
                >
                  <ToggleButton value="day">{translate('reports.groupBy.day', { fallback: 'Day' })}</ToggleButton>
                  <ToggleButton value="month">{translate('reports.groupBy.month', { fallback: 'Month' })}</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {/* Time Series Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1A1A1A',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {translate('reports.dailyBreakdown', { fallback: 'Daily Breakdown' })} ({sortedData.length} {translate('reports.days', { fallback: 'days' })})
                </Typography>

                {isMobile ? (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Paper variant="outlined" sx={mobileRowCardSx}>
                      <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                        {translate('reports.table.total', { fallback: 'Total' })}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        {canSeeTotalSessions && (
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(totals.session_count, 0)}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.charged_session_count, 0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.chargingHours', { fallback: 'Charging Hours' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.charging_hours, 1)} hrs</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {sortedData.map((row) => (
                      <Paper key={row.date} variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 0.75 }}>
                          {periodType === 'day'
                            ? translate('reports.table.date', { fallback: 'Date' })
                            : translate('reports.table.month', { fallback: 'Month' })
                          }
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 1 }}>
                          {periodType === 'day' ? formatDate(row.date, 'long') : row.date}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(row.session_count, 0)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.charged_session_count, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.chargingHours', { fallback: 'Charging Hours' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.charging_hours, 1)} hrs</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                      <Table stickyHeader sx={tableDetailsSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'date'}
                                direction={orderBy === 'date' ? order : 'asc'}
                                onClick={() => handleSort('date')}
                              >
                                {periodType === 'day'
                                  ? translate('reports.table.date', { fallback: 'Date' })
                                  : translate('reports.table.month', { fallback: 'Month' })
                                }
                              </TableSortLabel>
                            </TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'session_count'}
                                  direction={orderBy === 'session_count' ? order : 'asc'}
                                  onClick={() => handleSort('session_count')}
                                >
                                  {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                </TableSortLabel>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'charged_session_count'}
                                  direction={orderBy === 'charged_session_count' ? order : 'asc'}
                                  onClick={() => handleSort('charged_session_count')}
                                >
                                  {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                </TableSortLabel>
                                <MuiTooltip
                                  title={translate('reports.kpi.chargedSessionsTooltip', {
                                    fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                  })}
                                  arrow
                                  placement="top"
                                >
                                  <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                </MuiTooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'charging_hours'}
                                direction={orderBy === 'charging_hours' ? order : 'asc'}
                                onClick={() => handleSort('charging_hours')}
                              >
                                {translate('reports.table.chargingHours', { fallback: 'Charging Hours' })}
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((row) => (
                            <TableRow key={row.date}>
                              <TableCell>{periodType === 'day' ? formatDate(row.date, 'long') : row.date}</TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">{formatNumber(row.session_count, 0)}</TableCell>
                              )}
                              <TableCell align="right">{formatNumber(row.charged_session_count, 0)}</TableCell>
                              <TableCell align="right">{formatNumber(row.charging_hours, 1)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow
                            sx={{
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                fontWeight: 600,
                                zIndex: 2,
                              }
                            }}
                          >
                            <TableCell>{translate('reports.table.total', { fallback: 'Total' })}</TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">{formatNumber(totals.session_count, 0)}</TableCell>
                            )}
                            <TableCell align="right">{formatNumber(totals.charged_session_count, 0)}</TableCell>
                            <TableCell align="right">{formatNumber(totals.charging_hours, 1)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Charger Details Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1A1A1A',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {translate('reports.chargerDetails', { fallback: 'Charger Details' })}
                </Typography>

                {isMobile ? (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    {sortedChargers.map((charger) => (
                      <Paper key={charger.charger_id} variant="outlined" sx={mobileRowCardSx}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ ...mobileLabelSx, mb: 0.25 }}>{translate('reports.table.charger', { fallback: 'Charger' })}</Typography>
                            <Typography sx={{ ...mobileValueSx, fontWeight: 700 }}>
                              {charger.charger_name || charger.charger_id}
                            </Typography>
                          </Box>
                          <Chip
                            label={formatStatus(charger.status)}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              backgroundColor: STATUS_COLORS[charger.status] || '#9E9E9E',
                              color: '#FFFFFF',
                              flexShrink: 0,
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.utilization', { fallback: 'Utilization' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(charger.utilization_rate, 1)}%</Typography>
                          </Box>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(charger.session_count, 0)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(charger.charged_session_count, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.sessionsPerDay', { fallback: 'Sessions/Day' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(charger.sessions_per_day, 1)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.chargingHours', { fallback: 'Charging Hours' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(charger.total_charging_hours, 1)} hrs</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(charger.total_energy_kwh, 1)} kWh</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                      <Table stickyHeader sx={tableDetailsSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel
                                active={chargerOrderBy === 'charger_name'}
                                direction={chargerOrderBy === 'charger_name' ? chargerOrder : 'asc'}
                                onClick={() => handleChargerSort('charger_name')}
                              >
                                {translate('reports.table.charger', { fallback: 'Charger' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={chargerOrderBy === 'utilization_rate'}
                                direction={chargerOrderBy === 'utilization_rate' ? chargerOrder : 'asc'}
                                onClick={() => handleChargerSort('utilization_rate')}
                              >
                                {translate('reports.table.utilization', { fallback: 'Utilization' })}
                              </TableSortLabel>
                            </TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">
                                <TableSortLabel
                                  active={chargerOrderBy === 'session_count'}
                                  direction={chargerOrderBy === 'session_count' ? chargerOrder : 'asc'}
                                  onClick={() => handleChargerSort('session_count')}
                                >
                                  {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                </TableSortLabel>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <TableSortLabel
                                  active={chargerOrderBy === 'charged_session_count'}
                                  direction={chargerOrderBy === 'charged_session_count' ? chargerOrder : 'asc'}
                                  onClick={() => handleChargerSort('charged_session_count')}
                                >
                                  {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                </TableSortLabel>
                                <MuiTooltip
                                  title={translate('reports.kpi.chargedSessionsTooltip', {
                                    fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                  })}
                                  arrow
                                  placement="top"
                                >
                                  <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                </MuiTooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={chargerOrderBy === 'sessions_per_day'}
                                direction={chargerOrderBy === 'sessions_per_day' ? chargerOrder : 'asc'}
                                onClick={() => handleChargerSort('sessions_per_day')}
                              >
                                {translate('reports.table.sessionsPerDay', { fallback: 'Sessions/Day' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={chargerOrderBy === 'total_charging_hours'}
                                direction={chargerOrderBy === 'total_charging_hours' ? chargerOrder : 'asc'}
                                onClick={() => handleChargerSort('total_charging_hours')}
                              >
                                {translate('reports.table.chargingHours', { fallback: 'Charging Hours' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={chargerOrderBy === 'total_energy_kwh'}
                                direction={chargerOrderBy === 'total_energy_kwh' ? chargerOrder : 'asc'}
                                onClick={() => handleChargerSort('total_energy_kwh')}
                              >
                                {translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedChargers.map((charger) => (
                            <TableRow key={charger.charger_id} hover>
                              <TableCell>{charger.charger_name}</TableCell>
                              <TableCell align="right">{formatNumber(charger.utilization_rate, 1)}%</TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">{formatNumber(charger.session_count, 0)}</TableCell>
                              )}
                              <TableCell align="right">{formatNumber(charger.charged_session_count, 0)}</TableCell>
                              <TableCell align="right">{formatNumber(charger.sessions_per_day, 1)}</TableCell>
                              <TableCell align="right">{formatNumber(charger.total_charging_hours, 1)}</TableCell>
                              <TableCell align="right">{formatNumber(charger.total_energy_kwh, 1)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Charts Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                {/* Utilization Trends Chart */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3, mt: 3, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.charts.utilizationTrends', { fallback: 'Utilization Trends' })}
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <LineChart data={processedTimeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => periodType === 'month' ? date : formatDate(date)}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        interval={isMobile ? 'preserveStartEnd' : 0}
                        minTickGap={isMobile ? 24 : 12}
                      />
                      <YAxis
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        tickFormatter={(value) => formatNumber(value, 0)}
                        width={isMobile ? 42 : 54}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'charging_hours') return `${formatNumber(value, 1)} hrs`;
                          return formatNumber(value, 0);
                        }}
                        labelFormatter={(date) => periodType === 'month' ? date : formatDate(date, 'long')}
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E0E0E0',
                          borderRadius: 8,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                      <Line
                        type="monotone"
                        dataKey="session_count"
                        stroke="#009A75"
                        strokeWidth={isMobile ? 2 : 3}
                        dot={{ r: isMobile ? 3 : 4, fill: '#009A75' }}
                        name="Sessions"
                      />
                      <Line
                        type="monotone"
                        dataKey="charging_hours"
                        stroke="#2196F3"
                        strokeWidth={isMobile ? 1.75 : 2}
                        dot={{ r: isMobile ? 2.5 : 3, fill: '#2196F3' }}
                        name="Charging Hours"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, mb: 3, minWidth: 0 }}>
                  {/* Utilization by Charger Chart */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.utilizationByCharger', { fallback: 'Utilization by Charger' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
                      <BarChart data={topChargerChartData} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          tickFormatter={(value) => `${value}%`}
                        />
                        <YAxis
                          type="category"
                          dataKey="charger_name"
                          tick={{ fontSize: isMobile ? 10 : 11 }}
                          stroke="#606060"
                          width={isMobile ? 90 : 120}
                          tickFormatter={(value) => isMobile ? String(value).slice(0, 14) : String(value)}
                        />
                        <Tooltip
                          formatter={(value: number) => `${formatNumber(value, 1)}%`}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Bar dataKey="utilization_rate" fill="#009A75" name="Utilization %" radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Status Distribution Chart */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.statusDistribution', { fallback: 'Charger Status Distribution' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
                      <PieChart>
                        <Pie
                          data={data.status_distribution}
                          cx="50%"
                          cy="50%"
                          nameKey="status"
                          labelLine={false}
                          label={isMobile ? false : ({ status, count }) => `${formatStatus(status)}: ${count}`}
                          outerRadius={isMobile ? 75 : 100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {data.status_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => formatNumber(value, 0)}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Legend
                          verticalAlign={isMobile ? 'bottom' : 'top'}
                          align="center"
                          wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                          formatter={(value: string) => formatStatus(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
